import { AxiosError } from "axios";
import api from "../queries/api";
import { queryClient } from "../queries/client";
import { Credentials, UserInfo } from "../types/auth";
import { QueryFunctionContext } from "react-query";
import {
  EmailOTPType,
  InitiateRegisterType,
  LastUsedProductType,
  RegisterUserType,
  ResetPasswordType,
} from "../queries/auth";
import { ChangePasswordType } from "../layout/TopBar/components/ChangePasswordModal";
import { getAuthStore } from "../store/useAuthStore";
import { decodeAuthToken } from "../utils/auth";
import type {
  RegistrationResponseJSON,
  AuthenticationResponseJSON,
} from "@simplewebauthn/browser";

export type SharedContentType = {
  resourceId: string;
  resourceType: string;
  platform: string;
  isPrivate?: boolean;
  isLive?: boolean;
  companyId?: string;
  createdAt?: Date;
  updatedAt?: Date;
  userId?: string;
  resourceName?: string;
  companyName?: string;
};

export async function signOut(
  data: any
): Promise<{ message: string; data: string }> {
  return api.post(`auth/logout`, undefined).then((res: any) => res.data);
}

export async function signIn(
  signInDetails: Credentials & { redirect?: string }
): Promise<{ message: string; data: string }> {
  return api
    .post(`auth/login?redirect=${signInDetails.redirect}`, signInDetails)
    .then((res: any) => res.data);
}

export async function checkHeartBeat(): Promise<string> {
  return api
    .get("auth/heartbeat")
    .then((res: any) => {
      if (res.data.data) {
        const user = getAuthStore().accessToken;
        const userPayload = decodeAuthToken(user!);
        if (res.data.data.toString() !== userPayload.id.toString()) {
          sessionStorage.clear();
          queryClient.clear();
          getAuthStore().clear();
          window.location.reload();
        }
      }
      return res.data.data;
    })
    .catch((err: AxiosError) => {
      if (err.response?.status === 403) {
        sessionStorage.clear();
        queryClient.clear();
        getAuthStore().clear();
        window.location.href = "/login";
      }
    });
}

export async function refetchAccessToken(): Promise<string> {
  return api.get("auth/token").then((res: any) => res.data.data);
}

export async function googleAuthCallback(
  context: QueryFunctionContext
): Promise<{ message: string; data: any }> {
  const credential = context.queryKey[1] as string;
  return api
    .post(`auth/google/callback`, { code: credential })
    .then((res: any) => res.data);
}

export async function microsoftAuthCallback(
  context: QueryFunctionContext
): Promise<{ message: string; data: any }> {
  const credential = context.queryKey[1] as string;
  return api
    .post(`auth/microsoft/callback`, { code: credential })
    .then((res: any) => res.data);
}

export async function getUserInfo(context: QueryFunctionContext): Promise<UserInfo[]> {
  const email = context.queryKey[1] as string;
  return api
    // .get(`auth/userInfo?email=${encodeURIComponent(email)}`)
      .post(`auth/userInfo`, { email })
    .then((res: any) => res.data.data as UserInfo[]);
}

export async function generateOtp(
  data: EmailOTPType
): Promise<{ message: string; data: any }> {
  return api.post(`auth/generate-email-otp`, data).then((res: any) => res.data);
}
export async function forgotPassword(
  data: EmailOTPType
): Promise<{ message: string; data: any }> {
  return api.post(`auth/forgot-password`, data).then((res: any) => res.data);
}

export async function resetPassword({
  data,
}: {
  data: ResetPasswordType;
}): Promise<{ message: string; data: any }> {
  return api.post(`auth/reset-password`, data, {}).then((res: any) => res.data);
}

export async function changePassword({
  data,
  token,
}: {
  data: ChangePasswordType;
  token: string;
}): Promise<{ message: string; data: any }> {
  return api.post(`auth/change-password`, data).then((res: any) => res.data);
}

export async function createPassword({
  data,
}: {
  data: { newPassword: string; confirmNewPassword: string };
}): Promise<{ message: string; data: any }> {
  return api.post(`auth/create-password`, data).then((res: any) => res.data);
}

export async function getPlatformInfo(
  context: QueryFunctionContext
): Promise<any> {
  return api.get(`auth/user/platform`).then((res: any) => res.data.data);
}

export async function getSharedContentInfo(
  context: QueryFunctionContext
): Promise<any> {
  return api.get(`auth/user/sharedContent`).then((res: any) => res.data);
}

export async function sharedContentToken({
  data,
  token,
}: {
  data: SharedContentType;
  token: string;
}): Promise<{ message: string; data: any }> {
  return api
    .post(`auth/user/sharedContentToken`, { sharedResource: data })
    .then((res: any) => res.data);
}

export async function updateUserInfo({
  data,
}: {
  data: LastUsedProductType;
}): Promise<{ message: string; data: any }> {
  return api.post(`auth/user/user-info`, data, {}).then((res: any) => res.data);
}

export async function getUserProfileData(): Promise<any> {
  return api.get(`auth/user/profile`).then((res: any) => res.data);
}

export async function initiateRegister({
  data,
}: {
  data: InitiateRegisterType;
}): Promise<{ message: string; data: any }> {
  return api.post(`auth/register/initiate`, data, {}).then((res: any) => res.data);
}

export async function registerUser({
  data,
}: {
  data: RegisterUserType;
}): Promise<{ message: string; data: any }> {
  return api.post(`auth/register/complete`, data, {}).then((res: any) => res.data);
}

export async function registerWaitlist({
  data,
}: {
  data: { email: string };
}): Promise<{ message: string; data: any }> {
  return api
    .post(`auth/register-waitlist`, data, {})
    .then((res: any) => res.data);
}

export type PasskeyCredential = {
  id: string;
  credentialId: string;
  label?: string;
  deviceType: string;
  backedUp: boolean;
  createdAt: string;
};

export async function passkeyRegisterBegin(): Promise<{
  message: string;
  data: { transactionId: string; options: any };
}> {
  return api.post(`auth/passkey/register/begin`).then((res: any) => res.data);
}

export async function passkeyRegisterFinish(data: {
  transactionId: string;
  response: RegistrationResponseJSON;
  label?: string;
}): Promise<{ message: string; data: { credentialId: string } }> {
  return api
    .post(`auth/passkey/register/finish`, data)
    .then((res: any) => res.data);
}

export async function passkeyAuthenticateBegin(data?: {
  email?: string;
  userType?: string;
}): Promise<{ message: string; data: { transactionId: string; options: any } }> {
  return api
    .post(`auth/passkey/authenticate/begin`, data ?? {})
    .then((res: any) => res.data);
}

export async function passkeyAuthenticateFinish(data: {
  email?: string;
  userType?: string;
  authStrategyType: "PASSKEY";
  passkeyTransactionId: string;
  passkeyAuthenticationResponse: AuthenticationResponseJSON;
}): Promise<{ message: string; data: string }> {
  return api
    .post(`auth/passkey/authenticate/finish`, data)
    .then((res: any) => res.data);
}

export async function listPasskeys(): Promise<{
  message: string;
  data: PasskeyCredential[];
}> {
  return api.get(`auth/passkey`).then((res: any) => res.data);
}

export async function deletePasskey(
  credentialId: string
): Promise<{ message: string }> {
  return api
    .delete(`auth/passkey/${encodeURIComponent(credentialId)}`)
    .then((res: any) => res.data);
}
